import { getRecordValue } from 'src/modules/shared/utils/record.util';
import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import React from 'react';
import type { JSONSchema7 } from 'json-schema';
import { Text } from 'src/modules/shared/components/atoms/text';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import StepInputFieldCard from './step-input-field-card';
import {
  buildFields,
  inferModeFromInputs,
  type InputMappingMode,
  escapeTemplates,
  unescapeTemplates,
} from './step-inputs-mapping-utils';
import {
  inferDiscriminatorKey,
  inferVariantIndexFromInputs,
  getTopLevelObjectVariants,
} from './workflow-schema-utils';
import { type ReferenceOption, type StepOutputOption } from './types';

/** Step Inputs Mapping Editor Props contract. */
interface StepInputsMappingEditorProps {
  /** Identifier of the step being edited. */
  stepId: string;
  /** Action whose inputs are being configured. */
  action?: { name?: string; inputSchema?: Record<string, unknown> };
  /** Current input mappings for the selected step. */
  inputs: Record<string, unknown>;
  /** Workflow input names available for references. */
  workflowInputKeys: string[];
  /** Schema used to describe available workflow input values. */
  workflowInputSchema?: JSONSchema7;
  /** Available output values from other steps. */
  stepOutputs: StepOutputOption[];
  /** Selectable references for input mapping. */
  referenceOptions: ReferenceOption[];
  /** Updates the selected step input mappings. */
  onChangeInputs: (nextInputs: Record<string, unknown>) => void;
}

export const StepInputsMappingEditor: React.FC<
  StepInputsMappingEditorProps
> = ({
  stepId,
  action,
  inputs,
  workflowInputKeys,
  workflowInputSchema,
  stepOutputs,
  referenceOptions,
  onChangeInputs,
}) => {
  const variants = React.useMemo(
    () => getTopLevelObjectVariants(action?.inputSchema),
    [action?.inputSchema]
  );
  const variantsSignature = React.useMemo(
    () => variants.map((v) => v.id).join('|'),
    [variants]
  );
  const discriminatorKey = React.useMemo(
    () => inferDiscriminatorKey(variants),
    [variants]
  );
  const [variantIndex, setVariantIndex] = React.useState(0);

  const variantInitKey = React.useMemo(
    () => `${stepId}|${action?.name ?? ''}|${variantsSignature}`,
    [action?.name, stepId, variantsSignature]
  );
  const inferredVariantIndex = React.useMemo(
    () => inferVariantIndexFromInputs(inputs, variants),
    [inputs, variants]
  );
  const variantInitKeyRef = React.useRef<string>('');

  React.useEffect(() => {
    if (variants.length === 0) {
      variantInitKeyRef.current = variantInitKey;
      setVariantIndex(0);
      return;
    }
    if (variantInitKeyRef.current === variantInitKey) return;
    variantInitKeyRef.current = variantInitKey;
    setVariantIndex(inferredVariantIndex);
  }, [inferredVariantIndex, variantInitKey, variants.length]);

  const activeVariant =
    variants.length > 0 ? variants[variantIndex] ?? variants[0] : undefined;

  const discriminatorInitKeyRef = React.useRef<string>('');
  React.useEffect(() => {
    if (!activeVariant || !discriminatorKey) return;
    const desired = getRecordValue(activeVariant.constFields, discriminatorKey);
    if (desired === undefined) return;
    const current = inputs[discriminatorKey];

    const initKey = `${variantInitKey}|${discriminatorKey}`;
    if (discriminatorInitKeyRef.current === initKey) return;
    discriminatorInitKeyRef.current = initKey;

    if (current === undefined || current === '') {
      onChangeInputs({ ...inputs, [discriminatorKey]: desired });
    }
  }, [activeVariant, discriminatorKey, inputs, onChangeInputs, variantInitKey]);

  const effectiveInputSchema = React.useMemo(() => {
    if (activeVariant) return activeVariant.schema;
    return action?.inputSchema;
  }, [activeVariant, action?.inputSchema]);

  const fields = React.useMemo(
    () => buildFields(effectiveInputSchema, activeVariant),
    [activeVariant, effectiveInputSchema]
  );

  const visibleFields = React.useMemo(() => {
    if (!variants.length || !discriminatorKey) return fields;
    return fields.filter((f) => f.key !== discriminatorKey);
  }, [discriminatorKey, fields, variants.length]);

  const [modeByKey, setModeByKey] = React.useState<
    Partial<Record<string, InputMappingMode>>
  >({});

  const fieldsSignature = React.useMemo(
    () =>
      fields.map((f) => `${f.key}:${f.type}:${f.required ? 1 : 0}`).join('|'),
    [fields]
  );

  const initSignature = React.useMemo(
    () => `${stepId}|${action?.name ?? ''}|${fieldsSignature}`,
    [action?.name, fieldsSignature, stepId]
  );

  const lastInitSignatureRef = React.useRef<string>('');

  React.useEffect(() => {
    if (lastInitSignatureRef.current === initSignature) return;
    lastInitSignatureRef.current = initSignature;

    const { modes } = inferModeFromInputs(fields, inputs);
    setModeByKey(modes);
  }, [fields, initSignature, inputs]);

  const updateFieldValue = React.useCallback(
    (key: string, value: unknown) => {
      const next = { ...inputs };
      if (value === undefined) {
        delete next[key];
      } else {
        next[key] = value;
      }
      onChangeInputs(next);
    },
    [inputs, onChangeInputs]
  );

  const handleModeChange = React.useCallback(
    (key: string, nextMode: InputMappingMode) => {
      setModeByKey((prev) => ({ ...prev, [key]: nextMode }));

      const current = inputs[key];
      // Clear current value when switching modes to avoid leftover templates.
      updateFieldValue(key, undefined);
      if (nextMode === 'literal') {
        if (typeof current === 'string') {
          updateFieldValue(key, escapeTemplates(current));
        }
        return;
      }

      if (nextMode === 'workflow-input') {
        const first = workflowInputKeys[0];
        updateFieldValue(key, first ? `{{inputs.${first}}}` : undefined);
        return;
      }

      if (nextMode === 'step-output') {
        const firstWithOutputs = stepOutputs.find(
          (s) => s.outputKeys.length > 0
        );
        if (firstWithOutputs) {
          updateFieldValue(
            key,
            `{{steps.${firstWithOutputs.stepId}.outputs.${firstWithOutputs.outputKeys[0]}}}`
          );
        } else {
          updateFieldValue(key, undefined);
        }
        return;
      }

      {
        if (typeof current === 'string') {
          updateFieldValue(key, unescapeTemplates(current));
          return;
        }
        updateFieldValue(key, undefined);
      }
    },
    [inputs, stepOutputs, updateFieldValue, workflowInputKeys]
  );

  if (!fields.length) {
    return (
      <EmptyState
        description="Esta acción no tiene entradas configurables (o no se pudo leer su esquema)."
        appearance="soft"
        variant="neutral"
        size="sm"
        className="py-6"
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-1">
        <Text as="h4" size="small" weight="medium" className="text-slate-700">
          Mapeo de entradas
        </Text>
        <Text size="tiny" className="text-slate-500">
          {nonEmptyString(action?.name) ?? 'Acción'} · {visibleFields.length}{' '}
          campo(s)
        </Text>
      </div>

      {variants.length > 0 && activeVariant && (
        <div className="border border-slate-200 rounded-lg bg-white p-3 space-y-2">
          <Text size="tiny" weight="medium" className="text-slate-600">
            Formato de entrada (elige uno)
          </Text>
          <select
            className="border rounded px-2 py-1.5 text-sm w-full"
            value={activeVariant.id}
            onChange={(e) => {
              const nextId = e.target.value;
              const nextIdx = variants.findIndex((v) => v.id === nextId);
              const next = variants[nextIdx] ?? variants[0];
              setVariantIndex(nextIdx >= 0 ? nextIdx : 0);

              const allowed = new Set(Object.keys(next.properties));
              const pruned: Record<string, unknown> = {};
              Object.entries(inputs).forEach(([k, v]) => {
                if (allowed.has(k)) pruned[k] = v;
              });
              if (discriminatorKey) {
                const desired = getRecordValue(
                  next.constFields,
                  discriminatorKey
                );
                if (desired !== undefined) {
                  pruned[discriminatorKey] = desired;
                }
              }
              onChangeInputs(pruned);
            }}
          >
            {variants.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
          <Text size="tiny" className="text-slate-500">
            Esta acción usa un esquema tipo{' '}
            <span className="font-mono">anyOf/oneOf</span>; solo debe
            configurarse una opción.
          </Text>
        </div>
      )}

      <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
        {visibleFields.map((field) => (
          <StepInputFieldCard
            key={`${stepId}-${field.key}`}
            stepId={stepId}
            field={field}
            mode={modeByKey[field.key] ?? 'literal'}
            value={inputs[field.key]}
            workflowInputKeys={workflowInputKeys}
            workflowInputSchema={
              workflowInputSchema as Record<string, unknown> | undefined
            }
            stepOutputs={stepOutputs}
            referenceOptions={referenceOptions}
            onModeChange={handleModeChange}
            onClear={(key) => {
              setModeByKey((prev) => ({ ...prev, [key]: 'literal' }));
              updateFieldValue(key, undefined);
            }}
            onChangeValue={updateFieldValue}
          />
        ))}
      </div>
    </div>
  );
};

export default StepInputsMappingEditor;
